import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2, Home, Briefcase, Plane, Search, X } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import api from "@/lib/api";
import { toast } from "sonner";

const LABEL_PRESETS = [
  { label: "Home", icon: <Home className="w-3.5 h-3.5" /> },
  { label: "Work", icon: <Briefcase className="w-3.5 h-3.5" /> },
  { label: "Trip", icon: <Plane className="w-3.5 h-3.5" /> },
];

const useDebounced = (value, delay = 160) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

export const LocationDialog = ({ open, onOpenChange }) => {
  const {
    setLocation,
    saveLocation,
    savedLocations,
    removeSavedLocation,
    location,
  } = useApp();

  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(null);
  const [labelDraft, setLabelDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const debouncedQuery = useDebounced(query, 160);

  const { data: searchData } = useQuery({
    queryKey: ["zip-search", debouncedQuery],
    queryFn: () =>
      api
        .get("/zips/search", { params: { q: debouncedQuery } })
        .then((r) => r.data.results),
    enabled: debouncedQuery.trim().length >= 1,
  });
  const results = searchData || [];

  const triggerGps = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by this browser");
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        let loc = { lat, lon, city: "Current location", state: "PA", source: "gps" };
        try {
          const { data: nearest } = await api.get("/zips/nearest", {
            params: { lat, lon },
          });
          loc = { ...nearest, source: "gps" };
        } catch {
          // fall back to raw GPS coords
        }
        setLocation(loc);
        setLoading(false);
        onOpenChange(false);
        toast.success(
          `Searching near ${loc.city}${loc.zip ? ` (${loc.zip})` : ""}`
        );
      },
      () => {
        setLoading(false);
        toast.error("Couldn't get GPS — enter a ZIP or city");
      }
    );
  };

  const pickResult = (r) => setPicked({ ...r, source: "zip" });

  const applyLocation = (loc) => {
    setLocation(loc);
    onOpenChange(false);
    toast.success(`Searching near ${loc.city}${loc.zip ? ` (${loc.zip})` : ""}`);
  };

  const saveAndUse = () => {
    if (!picked) return;
    const saved = saveLocation(picked, labelDraft.trim() || picked.city);
    applyLocation(saved);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md rounded-2xl p-0 overflow-hidden"
        data-testid="location-dialog"
      >
        <DialogTitle className="sr-only">Choose a location</DialogTitle>

        <div className="px-6 pt-6 pb-4 border-b border-border">
          <div className="eyebrow mb-1">Search at</div>
          <h3 className="font-serif text-2xl">Pick a location</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Where you are, where you&apos;re going, or a saved spot — switch anytime.
          </p>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* GPS */}
          <Button
            data-testid="location-use-gps"
            disabled={loading}
            onClick={triggerGps}
            className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <MapPin className="w-4 h-4 mr-2" />
            )}
            Use my current location
          </Button>

          {/* Saved */}
          {savedLocations.length > 0 && (
            <div>
              <div className="eyebrow mb-2">Saved</div>
              <div className="flex flex-wrap gap-2">
                {savedLocations.map((s) => (
                  <div
                    key={s.id}
                    data-testid={`saved-loc-${s.id}`}
                    className="group inline-flex items-center"
                  >
                    <button
                      onClick={() => applyLocation(s)}
                      className={`pill pr-2 ${
                        location?.id === s.id ? "pill-primary" : ""
                      }`}
                    >
                      <MapPin className="w-3 h-3" strokeWidth={1.7} />
                      <span className="truncate max-w-[120px]">
                        {s.label}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        {s.zip || s.city}
                      </span>
                    </button>
                    <button
                      data-testid={`saved-loc-remove-${s.id}`}
                      onClick={() => removeSavedLocation(s.id)}
                      className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                      aria-label="Remove saved location"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Search */}
          <div>
            <div className="eyebrow mb-2">Search by ZIP or city</div>
            <div className="flex items-center gap-2 bg-card border border-border rounded-full px-4 h-11">
              <Search
                className="w-4 h-4 text-muted-foreground"
                strokeWidth={1.6}
              />
              <input
                data-testid="location-search-input"
                value={query}
                autoFocus
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPicked(null);
                }}
                placeholder="19611 · West Reading · 19102 · Philadelphia"
                className="flex-1 bg-transparent outline-none text-sm"
              />
            </div>

            {results.length > 0 && !picked && (
              <div className="mt-2 border border-border rounded-2xl overflow-hidden divide-y divide-border bg-card">
                {results.map((r) => (
                  <button
                    key={r.zip}
                    data-testid={`location-result-${r.zip}`}
                    onClick={() => pickResult(r)}
                    className="w-full text-left px-4 py-3 hover:bg-secondary/60 flex items-center justify-between"
                  >
                    <div>
                      <div className="font-medium text-sm">{r.city}</div>
                      <div className="text-xs text-muted-foreground">
                        {r.zip} · {r.state}
                      </div>
                    </div>
                    <MapPin
                      className="w-3.5 h-3.5 text-muted-foreground"
                      strokeWidth={1.6}
                    />
                  </button>
                ))}
              </div>
            )}

            {query.length > 0 && results.length === 0 && (
              <div className="mt-2 text-xs text-muted-foreground">
                No match in PA · v1 is Pennsylvania only.
              </div>
            )}
          </div>

          {/* Picked → label + save/use */}
          {picked && (
            <div
              data-testid="picked-location-card"
              className="rounded-2xl border border-primary/30 bg-primary/5 p-4 space-y-3"
            >
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-primary font-semibold">
                  Selected
                </div>
                <div className="font-serif text-xl mt-1">
                  {picked.city}{" "}
                  <span className="text-sm text-muted-foreground">
                    {picked.zip}
                  </span>
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-2">
                  Save as
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {LABEL_PRESETS.map((p) => (
                    <button
                      key={p.label}
                      data-testid={`location-preset-${p.label.toLowerCase()}`}
                      onClick={() => setLabelDraft(p.label)}
                      className={`pill ${
                        labelDraft === p.label ? "pill-primary" : ""
                      }`}
                    >
                      {p.icon} {p.label}
                    </button>
                  ))}
                </div>
                <Input
                  data-testid="location-label-input"
                  value={labelDraft}
                  onChange={(e) => setLabelDraft(e.target.value)}
                  placeholder="Custom label (optional)"
                  className="h-10 rounded-full px-4 text-sm"
                />
              </div>

              <div className="flex gap-2 pt-1">
                <Button
                  data-testid="location-save-and-use"
                  onClick={saveAndUse}
                  className="flex-1 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-11"
                >
                  Save & search here
                </Button>
                <Button
                  data-testid="location-use-once"
                  variant="outline"
                  onClick={() => applyLocation(picked)}
                  className="flex-1 rounded-full border-border bg-transparent h-11"
                >
                  Use once
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
